import { useState, useEffect } from 'react';
import { Layout, Button, Dropdown, Avatar, Badge, Space } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  SafetyOutlined,
  BulbOutlined,
  BulbFilled,
  BgColorsOutlined,
  RobotOutlined,
  ProfileOutlined,
  DownOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import useAuthStore from '../../store/authStore';
import useThemeStore from '../../store/themeStore';
import useLayoutStore from '../../store/layoutStore';
import useAIStore from '../../store/aiStore';
import { authApi } from '../../api/auth';
import Notification from '../common/Notification';
import OrganizationSwitcher from './OrganizationSwitcher';

const { Header: AntHeader } = Layout;

interface HeaderProps {
  collapsed: boolean;
  onToggle: () => void;
  onOpenCommandPalette?: () => void;
}

export default function Header({ collapsed, onToggle, onOpenCommandPalette }: HeaderProps) {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const { resolvedTheme, toggleTheme } = useThemeStore();
  const { toggleMobileMenu } = useLayoutStore();
  const { toggle: toggleAI } = useAIStore();
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size (< 768px)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isDark = resolvedTheme === 'dark';
  const headerBg = isDark ? '#1a1a1a' : '#f5f5f5';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(3, 7, 18, 0.1)';

  // Compute display name from user object
  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (user.name) return user.name;
    if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
    if (user.first_name) return user.first_name;
    if (user.email) return user.email;
    return 'User';
  };

  const displayName = getUserDisplayName();

  const handleLogout = async () => {
    try {
      await authApi.logout();
      clearAuth();
      navigate('/login');
      Notification.success({
        message: 'Logged out successfully',
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Clear auth anyway
      clearAuth();
      navigate('/login');
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '4px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{displayName}</div>
          <div style={{ fontSize: 12, opacity: 0.65 }}>{user?.email}</div>
        </div>
      ),
      disabled: true,
      style: { cursor: 'default' },
    },
    {
      type: 'divider',
    },
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: 'Profile',
      onClick: () => navigate('/settings/general'),
    },
    {
      key: 'security',
      icon: <SafetyOutlined />,
      label: 'Security',
      onClick: () => navigate('/settings/security'),
    },
    {
      key: 'theme',
      icon: <BgColorsOutlined />,
      label: 'Theme',
      onClick: () => navigate('/settings/theme'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  const notificationMenuItems: MenuProps['items'] = [
    {
      key: 'no-notifications',
      label: 'No new notifications',
      disabled: true,
    },
  ];

  return (
    <AntHeader
      role="banner"
      aria-label="Page header"
      style={{
        padding: isMobile ? '0 16px' : '0 24px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: headerBg,
        borderBottom: `1px solid ${borderColor}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexShrink: 0,
      }}
    >
      {/* Mobile: Hamburger menu | Desktop: Sidebar toggle */}
      {isMobile ? (
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={toggleMobileMenu}
          aria-label="Open navigation menu"
          aria-controls="navigation-sidebar"
          style={{
            fontSize: 18,
            width: 48,
            height: 48,
          }}
        />
      ) : (
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          aria-controls="navigation-sidebar"
          style={{
            fontSize: 16,
            width: 48,
            height: 48,
          }}
        />
      )}

      <nav aria-label="Utility navigation">
        <Space size="middle">
          <Button
            type="text"
            icon={<SearchOutlined style={{ fontSize: 18 }} />}
            onClick={onOpenCommandPalette}
            aria-label="Open search"
            title="Search (Ctrl+K)"
          />

          <Button
            type="text"
            icon={resolvedTheme === 'dark' ? <BulbFilled style={{ fontSize: 18 }} /> : <BulbOutlined style={{ fontSize: 18 }} />}
            onClick={toggleTheme}
            aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
          />

          <Button
            type="text"
            icon={<RobotOutlined style={{ fontSize: 18 }} />}
            onClick={toggleAI}
            aria-label="Open AI Assistant"
            title="AI Assistant"
          />

          <Dropdown
            menu={{ items: notificationMenuItems }}
            trigger={['click']}
            placement="bottomRight"
            overlayStyle={{
              position: 'fixed',
              top: '64px',
              right: '120px',
              transform: 'none',
              transition: 'opacity 0.2s',
            }}
          >
            <Badge count={0} size="small">
              <Button
                type="text"
                icon={<BellOutlined style={{ fontSize: 18 }} />}
                aria-label="Notifications"
                title="Notifications"
              />
            </Badge>
          </Dropdown>

          {/* Organization Switcher - CRITICAL for multi-tenant */}
          {!isMobile && <OrganizationSwitcher />}

          <Dropdown
            menu={{ items: userMenuItems }}
            trigger={['hover', 'click']}
            placement="bottomRight"
            overlayStyle={{
              position: 'fixed',
              top: '64px',
              right: '24px',
              transform: 'none',
              transition: 'opacity 0.2s',
            }}
          >
            <Space style={{ cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              {!isMobile && <span>{displayName}</span>}
              <DownOutlined style={{ fontSize: 12 }} />
            </Space>
          </Dropdown>
        </Space>
      </nav>
    </AntHeader>
  );
}
